import React, { Component } from "react"
import { graphql } from "@apollo/client/react/hoc"
import gql from "graphql-tag"
import DocumentList from "./DocumentList"
import "./DocumentsTab.css"

const IncidentDocumentOwnersQuery = gql`
    query IncidentDocumentOwnersQuery($incidentId: ID!, $includeCounts: Boolean!) {
        incident(incidentId: $incidentId) @include(if: $includeCounts) {
            id
            driver {
                id
            }
            car {
                id
            }
            incidentDocumentsCount: documentCount(slugKey: ["INCIDENT"])
            partyDocumentsCount: documentCount(slugKey: ["PARTY"])
            claimDocumentsCount: documentCount(slugKey: ["LIABILITY", "SUBROGATION"])
            inspectionDocumentsCount: documentCount(slugKey: ["INSPECTION"])
            driverDocumentsCount
            carDocumentsCount
        }
        allIncidentParties(incidentId: $incidentId) {
            edges {
                node {
                    id
                    pk
                    documentSlug
                    partyName
                    partyType
                }
            }
        }
        allLiabilityClaims(incidentId: $incidentId) {
            edges {
                node {
                    id
                    pk
                    documentSlug
                    externalId
                    tpa {
                        name
                    }
                }
            }
        }
        allSubrogationClaims(incidentId: $incidentId) {
            edges {
                node {
                    id
                    pk
                    documentSlug
                    externalId
                    tpa {
                        name
                    }
                }
            }
        }
    }
`

class DocumentListContainer extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isArchived: false,
            selectedSection: "general",
        }
    }

    getDocumentOwnerMap = () => {
        const ownerMap = {}

        const partyNodes = this.props.allIncidentParties?.edges?.map(({ node }) => node) || []
        partyNodes.forEach((node) => {
            if (node?.documentSlug) {
                ownerMap[node.documentSlug] = {
                    kind: "party",
                    label: `${node.pk || node.id} - ${node.partyName || "Unknown Party"} - ${node.partyType || "Unknown"}`,
                }
            }
        })

        const liabilityNodes = this.props.allLiabilityClaims?.edges?.map(({ node }) => node) || []
        liabilityNodes.forEach((node) => {
            if (node?.documentSlug) {
                ownerMap[node.documentSlug] = {
                    kind: "liability",
                    label: `${node.pk || node.id} - ${node.externalId || "No External ID"} - ${node.tpa?.name || "--"}`,
                }
            }
        })

        const subrogationNodes = this.props.allSubrogationClaims?.edges?.map(({ node }) => node) || []
        subrogationNodes.forEach((node) => {
            if (node?.documentSlug) {
                ownerMap[node.documentSlug] = {
                    kind: "subrogation",
                    label: `${node.pk || node.id} - ${node.externalId || "No External ID"} - ${node.tpa?.name || "--"}`,
                }
            }
        })

        return ownerMap
    }

    renderArchiveViewSelector = () => {
        const { isArchived } = this.state
        return (
            <div className="documents-view-toggle-wrap">
                <span className="documents-view-toggle-text">Exclude Archived</span>
                <label className="documents-archive-switch" aria-label="Toggle archived documents">
                    <input
                        type="checkbox"
                        checked={isArchived}
                        onChange={(e) => this.setState({ isArchived: e.target.checked })}
                    />
                    <span className="documents-archive-slider" />
                </label>
                <span className="documents-view-toggle-text">Show Archived</span>
            </div>
        )
    }

    renderScopedView = () => {
        const { id, refetchQuery, viewType, scope, slug, categoryId } = this.props
        const normalizedScope = (scope || "PARTY").toUpperCase()
        const isOtherDocumentScope = ["INSPECTION", "DRIVER", "CAR"].includes(normalizedScope)
        const category = isOtherDocumentScope ? null : normalizedScope
        const slugKey = normalizedScope === "INCIDENT" ? ["INCIDENT"] : normalizedScope === "CLAIM" ? ["LIABILITY", "SUBROGATION"] : null
        const documentOwnerMap = this.getDocumentOwnerMap()
        const otherDocumentsCategory = isOtherDocumentScope ? normalizedScope.toLowerCase() : null
        const otherDocumentsCategoryId = categoryId || (normalizedScope === "INSPECTION" ? id : null)
        return (
            <div className="document-tab-container" style={{ borderTopLeftRadius: "0px" }}>
                <DocumentList
                    id={id}
                    slug={slug || null}
                    isArchived={this.state.isArchived}
                    slugKey={slugKey}
                    documentOwnerMap={documentOwnerMap}
                    requireTargetSelection={normalizedScope === "INCIDENT"}
                    refetchQuery={refetchQuery || null}
                    viewType={viewType || null}
                    category={category}
                    showDocumentActions={!isOtherDocumentScope}
                    showIncidentDocuments={!isOtherDocumentScope}
                    showOtherDocumentsSection={isOtherDocumentScope}
                    otherDocumentsCategory={otherDocumentsCategory}
                    otherDocumentsCategoryId={otherDocumentsCategoryId}
                    archiveFilterControl={!isOtherDocumentScope ? this.renderArchiveViewSelector() : null}
                />
            </div>
        )
    }

    renderFullIncidentView = () => {
        const { id, refetchQuery, viewType } = this.props
        const documentOwnerMap = this.getDocumentOwnerMap()
        const counts = this.props.documentCounts || {}

        const sectionConfigMap = {
            general: { category: "INCIDENT", slugKey: ["INCIDENT"], showOtherDocuments: false },
            party: { category: "PARTY", slugKey: ["PARTY"], showOtherDocuments: false },
            claim: { category: "CLAIM", slugKey: ["LIABILITY", "SUBROGATION"], showOtherDocuments: false },
            inspection: { category: null, slugKey: null, showOtherDocuments: true, otherDocumentsCategory: "inspection", otherDocumentsCategoryId: id },
            driver: { category: null, slugKey: null, showOtherDocuments: true, otherDocumentsCategory: "driver", otherDocumentsCategoryId: this.props.incidentDriverId },
            car: { category: null, slugKey: null, showOtherDocuments: true, otherDocumentsCategory: "car", otherDocumentsCategoryId: this.props.incidentCarId },
        }
        const selectedSectionConfig = sectionConfigMap[this.state.selectedSection] || sectionConfigMap.general

        return (
            <div className="document-tab-container incident-docs-unified" style={{ borderTopLeftRadius: "0px" }}>
                <div className="incident-docs-layout">
                    <div className="incident-docs-sidebar">
                        <div className="incident-docs-sidebar-title">Documents</div>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "general" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "general" })}
                        >
                            <i className="fa fa-folder-open-o" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Incident Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.incident || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "party" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "party" })}
                        >
                            <i className="fa fa-users" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Party Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.party || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "claim" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "claim" })}
                        >
                            <i className="fa fa-balance-scale" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Claim Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.claim || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "inspection" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "inspection" })}
                        >
                            <i className="fa fa-camera" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Inspection Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.inspection || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "driver" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "driver" })}
                        >
                            <i className="fa fa-id-card-o" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Driver Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.driver || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "car" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "car" })}
                        >
                            <i className="fa fa-car" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Car Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.car || 0}</span>
                        </button>
                    </div>
                    <div className="incident-docs-content">
                        <DocumentList
                            id={id}
                            isArchived={this.state.isArchived}
                            slugKey={selectedSectionConfig.slugKey}
                            documentOwnerMap={documentOwnerMap}
                            requireTargetSelection={true}
                            refetchQuery={refetchQuery || null}
                            viewType={viewType || null}
                            category={selectedSectionConfig.category}
                            showDocumentActions={!selectedSectionConfig.showOtherDocuments}
                            showIncidentDocuments={!selectedSectionConfig.showOtherDocuments}
                            showOtherDocumentsSection={selectedSectionConfig.showOtherDocuments}
                            otherDocumentsCategory={selectedSectionConfig.otherDocumentsCategory}
                            otherDocumentsCategoryId={selectedSectionConfig.otherDocumentsCategoryId}
                            archiveFilterControl={selectedSectionConfig.showOtherDocuments ? null : this.renderArchiveViewSelector()}
                        />
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const { mode } = this.props
        if (mode === "full") return this.renderFullIncidentView()
        return this.renderScopedView()
    }
}

export default graphql(IncidentDocumentOwnersQuery, {
    options: ({ id, mode }) => ({ variables: { incidentId: id, includeCounts: mode === "full" }, fetchPolicy: "network-only" }),
    props: ({ data: { incident, allIncidentParties, allLiabilityClaims, allSubrogationClaims } }) => ({
        allIncidentParties,
        allLiabilityClaims,
        allSubrogationClaims,
        incidentDriverId: incident?.driver?.id || null,
        incidentCarId: incident?.car?.id || null,
        documentCounts: {
            incident: incident?.incidentDocumentsCount || 0,
            party: incident?.partyDocumentsCount || 0,
            claim: incident?.claimDocumentsCount || 0,
            inspection: incident?.inspectionDocumentsCount || 0,
            driver: incident?.driverDocumentsCount || 0,
            car: incident?.carDocumentsCount || 0,
        },
    }),
})(DocumentListContainer)
